import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { apiV2, ApiV2Error } from '@/lib/api-v2-client'
import { toast } from 'sonner'
import { SiteFooter } from '@/components/site-footer'

const REASONS = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'missing_features', label: 'Missing features' },
  { value: 'found_alternative', label: 'Found an alternative' },
  { value: 'other', label: 'Other' },
] as const

export const Route = createFileRoute('/goodbye')({
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: (search.token as string) || undefined,
  }),
  component: GoodbyePage,
  head: () => ({
    meta: [
      { title: 'Goodbye — Uptrack' },
      { name: 'description', content: 'Your Uptrack account has been scheduled for deletion.' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
})

function GoodbyePage() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()

  const [reason, setReason] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit() {
    if (!token) {
      navigate({ to: '/' })
      return
    }
    setSubmitting(true)
    try {
      await apiV2.submitGoodbyeFeedback({
        token,
        reason: reason ?? undefined,
        comment: comment.trim() || undefined,
      })
      toast.success('Thanks for the feedback')
    } catch (err) {
      // 409 already-submitted is treated as success on the backend; any other
      // error is non-blocking — the account is already soft-deleted.
      if (err instanceof ApiV2Error && err.status !== 409) {
        toast.error(err.message || 'Failed to submit feedback')
      }
    } finally {
      navigate({ to: '/' })
    }
  }

  function handleSkip() {
    navigate({ to: '/' })
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center px-4 py-12">
        <Card className="w-full">
          <CardHeader>
            <CardTitle className="text-2xl">We're sorry to see you go.</CardTitle>
            <CardDescription>
              Your account is scheduled for permanent deletion in 30 days. Contact support before
              then if you change your mind.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Why are you leaving? <span className="text-muted-foreground">(optional)</span></Label>
              <div className="flex flex-wrap gap-2">
                {REASONS.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setReason(reason === r.value ? null : r.value)}
                    className={`rounded-full border px-3 py-1.5 text-sm transition ${
                      reason === r.value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:bg-accent'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment">
                Anything we could've done better? <span className="text-muted-foreground">(optional)</span>
              </Label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={5}
                maxLength={4000}
                placeholder="Your feedback helps us build a better product."
                className="w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleSkip} disabled={submitting}>
                Skip
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit feedback'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
      <SiteFooter />
    </div>
  )
}
